import React, { useMemo } from 'react';

import { useGetFlowList, useGetMetricList } from '@src/emp-3.0/api/queryHooks';
import { formatStatus, formatTimestamp, isDeployed } from '@src/emp-3.0/utils';
import { IFlows, IMetric } from '@src/emp-3.0/types';
import StatusTag from '@src/emp-3.0/components/common/StatusTag';

import { InfoCardContainer, InfoLabel, InfoValue, MetricInfo, Wrapper } from './styled';
import { IInfoCardProps } from './typed';

const LABELS = {
  STATUS: 'Status',
  DEPLOYED_BY: 'Deploy by',
  LAST_UPDATED: 'Last Update',
  CREATED_BY: 'Create by',
} as const;

const INFO_VALUE_KEYS: Record<string, [string, string]> = {
  [LABELS.DEPLOYED_BY]: ['deployed_by', 'deployed_at'],
  [LABELS.LAST_UPDATED]: ['updated_by', 'updated_at'],
  [LABELS.CREATED_BY]: ['created_by', 'created_at'],
};

const InfoCard = ({ label, data, style }: IInfoCardProps) => {
  if (label === LABELS.STATUS) {
    const status = formatStatus(data?.status);
    const isDeploy = isDeployed(data);
    return (
      <InfoCardContainer style={style}>
        <InfoLabel>{label}:</InfoLabel>
        {status ? (
          <StatusTag type="primary" typeColor={isDeploy ? 'green' : 'orange'} size="small">
            {status}
          </StatusTag>
        ) : (
          <span>--</span>
        )}
      </InfoCardContainer>
    );
  }

  const valueKeys = label ? INFO_VALUE_KEYS[label] : undefined;

  if (!valueKeys) return null;

  const [key1, key2] = valueKeys;

  const val1 = data?.[key1];
  const val2 = data?.[key2];

  const allEmpty = !val1 && !val2;

  return (
    <InfoCardContainer style={style}>
      <InfoLabel>{label}:</InfoLabel>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <InfoValue>{val1 || '--'}</InfoValue>
        {allEmpty ? <InfoValue>/</InfoValue> : null}
        <InfoValue>{formatTimestamp(val2, 'YYYY/MM/DD HH:mm:ss') || '--'}</InfoValue>
      </div>
    </InfoCardContainer>
  );
};

const MetricTopInfo = React.memo(
  ({ metricId, type }: { metricId: number; type: 'metric' | 'flow' }) => {
    const { data: metricList = [] } = useGetMetricList();
    const { data: flowList = [] } = useGetFlowList();

    const data = useMemo(() => {
      let list: IFlows[] | IMetric[] = metricList;
      if (type === 'flow') {
        list = flowList;
      }
      return list.find((metric) => metric.id === metricId);
    }, [metricList, type, flowList, metricId]);

    if (!data) {
      return null;
    }

    return (
      <MetricInfo>
        <InfoCard label={LABELS.STATUS} data={data} style={{ flex: 1 }} />
        <InfoCard label={LABELS.DEPLOYED_BY} data={data} />
        <InfoCard label={LABELS.LAST_UPDATED} data={data} />
        <InfoCard label={LABELS.CREATED_BY} data={data} />
      </MetricInfo>
    );
  },
);

MetricTopInfo.displayName = 'MetricTopInfo';

export default MetricTopInfo;
